/**
 * @genesis_domain Shared Schemas — Request Validation
 * @genesis_version V9.1
 * @genesis_intent Request body, query param, and path param Zod validation schemas for all API endpoints.
 * @genesis_boundaries FORBIDDEN from containing response schemas. Pure validation contracts only.
 * @genesis_ai_trail Sharded from api.ts during v4.1 Cartography Cleanup (Strike 4).
 */

import { z } from 'zod';

import { CategoryFormSchema } from './category.js';
import { RecipeSchema } from './recipe.js';

// Recipe Create Schema
export const RecipeCreateSchema = RecipeSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});

export type RecipeCreate = z.infer<typeof RecipeCreateSchema>;

// AI Analysis Request
export const AnalyzeRecipeRequestSchema = z.object({
    title: z.string().min(1, 'Recipe title is required'),
    ingredients: z.array(
        z.object({
            name: z.string().min(1, 'Ingredient name is required'),
            quantityDisplay: z.string().nullable().optional(),
            unitText: z.string().nullable().optional(),
        })
    ).default([]),
    instructions: z.string().optional().default(''),
    workspaceId: z.string().uuid().optional(),
    servings: z.coerce.number().int().min(0).optional(),
    recipeId: z.string().uuid().optional(),
});

export type AnalyzeRecipeRequest = z.infer<typeof AnalyzeRecipeRequestSchema>;

// Import Recipe Request Schema
export const ImportRecipeRequestSchema = z.object({
    input: z.string().min(1, 'Input is required'),
    workspaceId: z.string().uuid().optional(),
});

export type ImportRecipeRequest = z.infer<typeof ImportRecipeRequestSchema>;

// Extract Vision Request Schema (Phase 4: Multimodal Intake)
export const ExtractVisionRequestSchema = z.object({
    images: z.array(z.string()).min(1, 'At least one image is required').max(5, 'Maximum 5 images allowed'),
    workspaceId: z.string().uuid().optional(),
});

export type ExtractVisionRequest = z.infer<typeof ExtractVisionRequestSchema>;

// Extract Text Recipe Request Schema (Phase 1: Messy Text Import)
export const ExtractTextRecipeRequestSchema = z.object({
    text: z.string().min(50, 'Recipe text must be at least 50 characters'),
    workspaceId: z.string().uuid().optional(),
});

export type ExtractTextRecipeRequest = z.infer<typeof ExtractTextRecipeRequestSchema>;

// Workspace Batch Operations
export const WorkspaceBatchLink = z.object({
    recipeIds: z.array(z.string().uuid()),
});

export type WorkspaceBatchLinkType = z.infer<typeof WorkspaceBatchLink>;

export const WorkspaceBatchUnlink = z.object({
    recipeIds: z.array(z.string().uuid()),
});

export type WorkspaceBatchUnlinkType = z.infer<typeof WorkspaceBatchUnlink>;

// Batch Category Assignment
export const BatchCategoryAssignSchema = z.object({
    recipeIds: z.array(z.string().uuid()).min(1, 'At least one recipe is required'),
    categoryIds: z.array(z.string().uuid()).min(1, 'At least one category is required'),
    source: z.enum(['user', 'ai', 'web']).default('user'),
});

export type BatchCategoryAssign = z.infer<typeof BatchCategoryAssignSchema>;

// Link Recipe Schema
export const LinkRecipeSchema = z.object({
    recipeId: z.string().uuid(),
});

export type LinkRecipe = z.infer<typeof LinkRecipeSchema>;

// Add Member By Email Schema
export const AddMemberByEmailSchema = z.object({
    email: z.string().email(),
    role: z.enum(['admin', 'member']),
});

export type AddMemberByEmail = z.infer<typeof AddMemberByEmailSchema>;

// Generate Invite Schema
export const GenerateInviteSchema = z.object({
    role: z.enum(['admin', 'member']),
    expiresInDays: z.number().int().min(1).max(30),
});

export type GenerateInvite = z.infer<typeof GenerateInviteSchema>;

// Auth Schemas
export const AuthLoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});

export type AuthLogin = z.infer<typeof AuthLoginSchema>;

export const AuthRegisterSchema = z.object({
    email: z.string().email(),
    password: z.string().min(6),
    name: z.string().min(1),
});

export type AuthRegister = z.infer<typeof AuthRegisterSchema>;

// Category Create Schema (alias for CategoryFormSchema)
export const CategoryCreateSchema = CategoryFormSchema;
export type CategoryCreate = z.infer<typeof CategoryCreateSchema>;

// Param & Query Validation Schemas
export const IdParamSchema = z.object({
    id: z.string().uuid(),
});

export type IdParam = z.infer<typeof IdParamSchema>;

export const WorkspaceIdQuerySchema = z.object({
    workspaceId: z.string().uuid().optional(),
});

export type WorkspaceIdQuery = z.infer<typeof WorkspaceIdQuerySchema>;

export const SearchQuerySchema = z.object({
    q: z.string().optional(),
});

export type SearchQuery = z.infer<typeof SearchQuerySchema>;

export const TokenParamSchema = z.object({
    token: z.string().min(10),
});

export type TokenParam = z.infer<typeof TokenParamSchema>;

export const WorkspaceMemberParamSchema = z.object({
    id: z.string().uuid(),
    memberId: z.string().uuid(),
});

export type WorkspaceMemberParam = z.infer<typeof WorkspaceMemberParamSchema>;

export const WorkspaceRecipeParamSchema = z.object({
    id: z.string().uuid(),
    recipeId: z.string().uuid(),
});

export type WorkspaceRecipeParam = z.infer<typeof WorkspaceRecipeParamSchema>;

// Batch Operations
export const BatchDeleteSchema = z.object({
    recipeIds: z.array(z.string().uuid()).min(1, 'At least one recipe ID is required'),
});

export type BatchDeleteInput = z.infer<typeof BatchDeleteSchema>;

export const BatchJsonLdExportBodySchema = z.object({
    recipeIds: z
        .array(z.string().uuid())
        .min(1, 'At least one recipeId is required')
        .max(1000, 'Maximum 1,000 recipes per JSON-LD batch to protect Worker memory'),
});

export type BatchJsonLdExportBody = z.infer<typeof BatchJsonLdExportBodySchema>;

export const BatchPdfExportBodySchema = z.object({
    recipeIds: z
        .array(z.string().uuid())
        .min(1, 'At least one recipeId is required')
        .max(20, 'Maximum 20 recipes per PDF batch to prevent server timeout'),
});

export type BatchPdfExportBody = z.infer<typeof BatchPdfExportBodySchema>;

export const BatchExportBodySchema = z.object({
    recipeIds: z.array(z.string().uuid()).min(1, 'At least one recipeId is required').max(500, 'Maximum 500 recipes per batch'),
});

export type BatchExportBody = z.infer<typeof BatchExportBodySchema>;

// ── Recipe List Sort Params ────────────────────────────────────────────────
// Used by GET /api/recipes to sort the vault list. Fields matching
// recipe_nutrition columns are sorted via foreign table embedding.
export const RecipeSortSchema = z.object({
    sortField: z
        .enum([
            'title',
            'created_at',
            'updated_at',
            'total_time',
            'prep_time',
            'cook_time',
            'servings',
            'calories',
            'protein_g',
            'carbs_g',
            'fat_g',
            'fiber_g',
            'sugar_g',
            'sodium_mg',
            'source_name',
            'source_author',
            'source_rating',
            'source_review_count',
            'rating',
        ])
        .optional()
        .default('created_at'),
    sortDirection: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type RecipeSortParams = z.infer<typeof RecipeSortSchema>;
